import { useState } from 'react';
import { ArrowLeft, Check, Plus, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { useGenericImageUpload } from '../../../hooks/useGenericImageUpload';
import { FOLDER_IMAGES, MAX_FILE_SIZE, MAX_IMAGES } from '../../onboarding/const/products';

export interface ConfirmScrapedProductViewProps {
  title: string;
  subtitle?: string;
  name: string;
  description: string;
  images: string[];
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onImagesUpdate: (images: string[]) => void;
  onBack: () => void;
  onConfirm: () => void;
  maxImages?: number;
}

export function ConfirmScrapedProductView({
  title,
  subtitle,
  name,
  description,
  images,
  onNameChange,
  onDescriptionChange,
  onImagesUpdate,
  onBack,
  onConfirm,
  maxImages = MAX_IMAGES,
}: ConfirmScrapedProductViewProps) {
  const { language } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const {
    isLoading: isUploadingImages,
    handleImageUpload,
    removeImage: handleRemoveImageFromHook,
  } = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages: images,
    onImagesUpdate: (newImages) => {
      onImagesUpdate(newImages);
      setSelectedImageIndex(0);
    },
    allowMultiple: maxImages > 1,
    maxImages,
    maxFileSize: MAX_FILE_SIZE,
  });

  const setMainImage = (index: number) => {
    if (index === 0) return;
    const reordered = [...images];
    const [item] = reordered.splice(index, 1);
    reordered.unshift(item);
    onImagesUpdate(reordered);
    setSelectedImageIndex(0);
  };

  const handleRemoveImage = (index: number) => {
    handleRemoveImageFromHook(index);
    setSelectedImageIndex((prev) =>
      prev >= index && prev > 0 ? prev - 1 : prev === index ? 0 : prev
    );
  };

  const handleConfirm = () => {
    const trimmedName = (name || '').trim();
    const trimmedDescription = (description || '').trim();
    const hasImages = images.length > 0;

    if (!hasImages) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Add at least one image.',
        { id: 'confirm-product-validation' }
      );
      return;
    }
    if (!trimmedName) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Product name is required.',
        { id: 'confirm-product-validation' }
      );
      return;
    }
    if (!trimmedDescription) {
      toast.error(
        dictionary['product-information-incomplete']?.[language] ?? 'Product description is required.',
        { id: 'confirm-product-validation' }
      );
      return;
    }

    onConfirm();
  };

  const mainImageUrl = images[selectedImageIndex] || images[0];
  const changeImageLabel = dictionary['upload-photo']?.[language] ?? 'Cambiar';

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto pb-12 pt-[60px] gap-8">
      <div className="flex flex-col gap-2 items-center text-center">
        <h2 className="font-semibold leading-tight text-[32px] text-slate-900 tracking-[-0.5px]">
          {title}
        </h2>
        {subtitle && (
          <p className="text-slate-500 text-[16px]">{subtitle}</p>
        )}
      </div>

      <div className="w-full rounded-[24px] flex flex-col md:flex-row gap-6 border border-slate-100 items-start p-6">
        <div className="flex flex-col gap-3 w-full md:w-[200px] md:max-w-[200px] shrink-0">
          <div className="aspect-square w-full rounded-[16px] overflow-hidden bg-white border border-slate-200 shadow-sm relative group cursor-pointer">
            {mainImageUrl ? (
              <>
                <div className="absolute inset-0">
                  <img
                    src={mainImageUrl}
                    alt={name || 'Product'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <label className="absolute inset-0 flex flex-col items-center justify-center bg-[#0F172A]/40 opacity-0 group-hover:opacity-100 transition-opacity z-10 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple={maxImages > 1}
                    onChange={handleImageUpload}
                    disabled={isUploadingImages}
                    className="hidden"
                  />
                  {isUploadingImages ? (
                    <div className="bg-white/90 p-2 rounded-full shadow-sm backdrop-blur-sm pointer-events-none">
                      <span className="w-5 h-5 border-2 border-slate-200 border-t-[#3E3EFF] rounded-full animate-spin block" />
                    </div>
                  ) : (
                    <div className="bg-white/90 p-2 rounded-full shadow-sm backdrop-blur-sm pointer-events-none">
                      <Upload className="size-5 text-[#3E3EFF]" />
                    </div>
                  )}
                  <span className="text-[12px] font-medium text-white mt-1 drop-shadow-md pointer-events-none">
                    {isUploadingImages
                      ? (dictionary?.['uploading-images']?.[language] ?? 'Cargando...')
                      : changeImageLabel}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveImage(selectedImageIndex);
                  }}
                  className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-full shadow-sm backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100 z-20"
                  title={dictionary['delete']?.[language] ?? 'Eliminar imagen'}
                >
                  <Trash2 className="size-4" />
                </button>
              </>
            ) : (
              <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer p-8">
                <input
                  type="file"
                  accept="image/*"
                  multiple={maxImages > 1}
                  onChange={handleImageUpload}
                  disabled={isUploadingImages}
                  className="hidden"
                />
                {isUploadingImages ? (
                  <>
                    <div className="bg-white/90 p-2 rounded-full shadow-sm backdrop-blur-sm mb-2">
                      <span className="w-6 h-6 border-2 border-slate-200 border-t-[#3E3EFF] rounded-full animate-spin block" />
                    </div>
                    <span className="text-sm font-medium text-slate-600">
                      {dictionary?.['uploading-images']?.[language] ?? 'Cargando imágenes...'}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="bg-white/90 p-2 rounded-full shadow-sm backdrop-blur-sm mb-2">
                      <Upload className="size-5 text-[#3E3EFF]" />
                    </div>
                    <span className="text-sm font-medium text-slate-600">
                      {dictionary['upload-photo']?.[language] ?? 'Subir foto'}
                    </span>
                    <span className="text-xs text-slate-400 mt-0.5">
                      {dictionary['accepted-formats']?.[language] ?? 'PNG, JPG, WEBP'}
                    </span>
                  </>
                )}
              </label>
            )}
          </div>

          {images.length > 0 && (
            <div className="space-y-2">
              <div className="flex gap-2 w-full overflow-x-auto pb-1">
                {images.map((src, index) => (
                  <button
                    key={`${index}-${src?.slice(-20)}`}
                    type="button"
                    onClick={() => setMainImage(index)}
                    className={`size-[44px] shrink-0 rounded-[10px] overflow-hidden cursor-pointer border-2 transition-all relative ${
                      index === selectedImageIndex
                        ? 'border-[#3E3EFF] shadow-[0_0_0_2px_rgba(62,62,239,0.1)]'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {images.length < maxImages && (
                  <label
                    className={`size-[44px] shrink-0 rounded-[10px] border-2 border-dashed flex items-center justify-center transition-colors ${
                      isUploadingImages
                        ? 'cursor-wait border-slate-200 bg-slate-50'
                        : 'border-slate-200 hover:border-[#3E3EFF] hover:bg-slate-50 cursor-pointer text-slate-400 hover:text-[#3E3EFF]'
                    }`}
                    title={dictionary['add-image']?.[language] ?? 'Añadir imagen'}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      multiple={maxImages > 1}
                      onChange={handleImageUpload}
                      disabled={isUploadingImages}
                      className="hidden"
                    />
                    {isUploadingImages ? (
                      <span className="w-5 h-5 border-2 border-slate-200 border-t-[#3E3EFF] rounded-full animate-spin block" />
                    ) : (
                      <Plus className="size-5" />
                    )}
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-4 w-full">
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wider ml-1">
              {dictionary['product-name']?.[language] ?? 'Nombre'}
            </label>
            <input
              type="text"
              placeholder={dictionary['product-name']?.[language] ?? 'Nombre del producto'}
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="w-full font-bold text-[18px] text-[#0F172A] leading-tight bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-[#3E3EFF] focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all placeholder:text-slate-300"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wider ml-1">
              {dictionary['product-description']?.[language] ?? 'Descripción'}
            </label>
            <textarea
              placeholder={dictionary['product-description']?.[language] ?? 'Descripción del producto...'}
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className="w-full text-[14px] text-slate-600 leading-relaxed bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-[#3E3EFF] focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all resize-none min-h-[140px] placeholder:text-slate-300"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-4 w-full">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 h-[56px] rounded-[64px] border border-slate-200 bg-white hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 text-slate-600 font-semibold text-[16px]"
        >
          <ArrowLeft className="size-5" />
          {dictionary['back']?.[language] ?? 'Volver'}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="flex-1 h-[56px] rounded-[64px] bg-[#3E3EFF] hover:bg-[#3232CC] shadow-[0px_0px_14px_0px_rgba(56,56,233,0.2)] transition-colors flex items-center justify-center gap-2 text-white font-bold text-[16px]"
        >
          {dictionary['confirm-data']?.[language] ?? 'Confirmar datos'}
          <Check className="size-5" />
        </button>
      </div>
    </div>
  );
}
